import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { MeResponseV2 } from '../../api/authorisation/models/api/me-response-v-2';
import { UsersService } from '../../api/authorisation/services';
import { isReadOnly } from '../../functions/is-read-only';
import { ServiceProviderName } from '../../model/service-provider-name';
import { selectIsAdminRoute, selectRouteBoardId, selectRouteOrganisationId } from '../router/router.selectors';
import { User, UserState } from './user.types';

export const userInitialState: UserState = {
  error: null,
  user: null,
  isUserLoading: false,
  isReadOnly: false,
};

const toUser = ({ activeState, globalRoles, serviceProviders, userDisplayName, userId }: MeResponseV2): User => {
  return {
    ...(activeState ? { activeState } : {}),
    ...(globalRoles ? { globalRoles } : {}),
    serviceProviders,
    tenants: serviceProviders?.flatMap(
      (sp) =>
        sp?.tenants?.map((t) => ({
          ...t,
          serviceProviderId: sp.id,
          serviceProviderName: sp.name as ServiceProviderName,
          ...(sp.role ? { serviceProviderUserRole: sp.role } : {}),
        })) ?? []
    ),
    userDisplayName,
    userId,
  };
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(userInitialState),
  withMethods((store) => {
    const authenticationService = inject(OidcSecurityService);
    const usersService = inject(UsersService);
    const oldStore = inject(Store);

    return {
      clearUser: () => patchState(store, userInitialState),
      loadUser: rxMethod<boolean | void>(
        switchMap((force?: boolean | void) => {
          patchState(store, { isUserLoading: true });
          return combineLatest({
            boardId: oldStore.select(selectRouteBoardId),
            tenantId: oldStore.select(selectRouteOrganisationId),
            isAdminRoute: oldStore.select(selectIsAdminRoute),
          }).pipe(
            switchMap(({ boardId, tenantId, isAdminRoute }) => {
              const user = store.user();
              if (user && !force) {
                return of({ user, boardId, tenantId, isAdminRoute });
              }

              const isAuthenticated = authenticationService.authenticated();
              if (!isAuthenticated) {
                return of({ user: null, boardId, tenantId, isAdminRoute });
              }

              return usersService.getMe().pipe(
                map(toUser),
                map((user) => ({ user, boardId, tenantId, isAdminRoute }))
              );
            }),
            switchMap(({ user, boardId, tenantId, isAdminRoute }) => {
              if (!user || isAdminRoute) {
                return of({ user, isReadOnly: false });
              }

              if (!boardId || !tenantId) {
                return of({ user, isReadOnly: true });
              }

              return of({ user, isReadOnly: isReadOnly(user, tenantId, boardId) });
            }),
            tapResponse({
              next: ({ user, isReadOnly }) => {
                patchState(store, { user, isUserLoading: false, isReadOnly });
              },
              error: (error: Error | HttpErrorResponse) => {
                console.error('Error loading user:', error);
                patchState(store, { isUserLoading: false, error, isReadOnly: true }); // Default to read-only on error
              },
            })
          );
        })
      ),
    };
  })
);
